import useModal from '__components/Modals/useModal';
import { toastVariant } from '__components/ToastProvider';
import { useToast } from '__components/ToastProvider';
import { useUploadFile } from '__hooks/uploadFiles/useUploadFile';
import SmartConnectorService from '__services/SmartConnectorService';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SMARTCONNECTORS_S3_SOURCES } from '../../constants';
import { useFiles } from '__hooks/uploadFiles/useFiles';
import {
  ScriptFile,
  SmartConnector,
  SmartConnectorExecution,
  SmartConnectorExtended,
} from '../../types';
import { getOriginalError } from '__services/AxiosService';
import { AxiosError } from 'axios';
import { RunSmartConnectorModalProps } from '.';

// TODO (keegandonley) - see below
// const shouldErrorShowWarning = (err: AxiosError) => {
//   const status = err.response?.status ?? 0;
//   return status >= 500;
// };

const FILE_UPLOAD_FAILED = 'FILE_UPLOAD_FAILED';

export const useRunSmartConnectorModal = (
  onStartRun?: () => void
): {
  runSmartConnectorModalProps: RunSmartConnectorModalProps;
  showRunSmartConnectorModal: (props: {
    smartConnector: SmartConnector | SmartConnectorExtended;
    execution?: SmartConnectorExecution | null;
    isDryRun?: boolean;
    canSkipDiffCheck?: boolean;
  }) => void;
} => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [showToast] = useToast();

  const [smartConnector, setSmartConnector] = useState<SmartConnector | null>(
    null
  );
  const [isDryRun, setIsDryRun] = useState<boolean>(false);
  const [skipDiffCheck, setSkipDiffCheck] = useState(false);
  const [canSkipDiffCheck, setCanSkipDiffCheck] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [persistentErrorMessage, setPersistentErrorMessage] = useState('');
  const [execution, setExecution] = useState<SmartConnectorExecution | null>(
    null
  );

  const uploadFile = useUploadFile(
    false,
    null,
    SMARTCONNECTORS_S3_SOURCES.IMPORT
  );

  const [, { addFiles }] = useFiles({
    uploadFile,
  });

  const handleSubmit = async () => {
    setShowTimeoutWarning(false);
    setPersistentErrorMessage('');

    let uploadedFile: ScriptFile | null = null;

    if (!execution && file instanceof File) {
      const createdFiles = await addFiles([file]);
      if (createdFiles.length) {
        uploadedFile = createdFiles[0];
      } else {
        throw new Error(FILE_UPLOAD_FAILED);
      }
    }

    await SmartConnectorService.startSmartConnectorFlow(
      smartConnector?.id,
      execution
        ? {
            reprocess_execution_id: execution.id,
            is_dry_run: isDryRun,
            disable_diff_check: skipDiffCheck,
          }
        : {
            source_file_id: uploadedFile?.id,
            is_dry_run: isDryRun,
            disable_diff_check: skipDiffCheck,
          }
    );

    onStartRun?.();

    showToast({
      message: `${execution ? t('Execution has been reprocessed') : isDryRun ? t('Dry run started.') : t('File processing started')} ${t("You'll be emailed on completion.")}`,
      variant: toastVariant.SUCCESS,
    });
  };

  const handleError = (err: AxiosError) => {
    if (err.message === FILE_UPLOAD_FAILED) {
      showToast({
        message: t(
          'File upload failed. Please try again or contact Kizen support.'
        ),
        variant: toastVariant.FAILURE,
      });
      return;
    }

    const originalError = getOriginalError(err);

    // if (shouldErrorShowWarning(err)) {
    // For now, show the warning on all errors. In the future (TODO keegandonley), we want to only show this
    // if the connection timed out.

    setShowTimeoutWarning(true);
    setPersistentErrorMessage(originalError?.detail || '');
    // }

    showToast({
      message:
        originalError?.detail ||
        (isDryRun
          ? t('Dry run failed to start.')
          : t('File processing failed to start')),
      variant: toastVariant.FAILURE,
    });
  };

  const handleHide = () => {
    setFile(null);
    setSmartConnector(null);
    setIsDryRun(false);
    setSkipDiffCheck(false);
    setCanSkipDiffCheck(false);
    setShowTimeoutWarning(false);
    setPersistentErrorMessage('');
    setExecution(null);
  };

  const [modalProps, , { show }] = useModal({
    handleSubmit,
    handleHide,
    handleError,
  });

  const showRunSmartConnectorModal = useCallback(
    async ({
      smartConnector,
      execution = null,
      isDryRun = false,
      canSkipDiffCheck = false,
    }: {
      smartConnector: SmartConnector | SmartConnectorExtended;
      execution?: SmartConnectorExecution | null;
      isDryRun?: boolean;
      canSkipDiffCheck?: boolean;
    }) => {
      setSmartConnector({
        ...smartConnector,
        custom_object:
          smartConnector.custom_object &&
          typeof smartConnector.custom_object === 'object'
            ? smartConnector.custom_object.id
            : smartConnector.custom_object,
      });
      setIsDryRun(isDryRun);
      setCanSkipDiffCheck(canSkipDiffCheck);
      setExecution(execution);
      show();

      if (execution?.input_file) {
        setSkipDiffCheck(execution.disable_diff_check ?? false);
        try {
          const response = await fetch(execution.input_file.url, {
            credentials: 'include',
            headers: { Range: 'bytes=0-3' },
          });
          const blob = await response.blob();
          setFile(
            new File([blob], execution.input_file.name, { type: blob.type })
          );
        } catch (err) {
          showToast({
            message: t('Failed to load the input file.'),
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [show, t, showToast]
  );

  return {
    runSmartConnectorModalProps: {
      ...modalProps,
      file,
      setFile,
      isDryRun,
      skipDiffCheck,
      setSkipDiffCheck,
      canSkipDiffCheck,
      smartConnector,
      showTimeoutWarning,
      persistentErrorMessage,
      isReprocess: !!execution,
    },
    showRunSmartConnectorModal,
  };
};
